import { useSuspenseQuery } from '@apollo/client'
import { ChevronsUpDown, LogOut, UserRound } from 'lucide-react'
import { Suspense, use } from 'react'
import { AuthDispatchContext } from '@/components/auth/context'
import { USER_NAV_QUERY } from '@/components/layout/side-nav/user-nav.queries'
import { SidebarUserNavFallback } from '@/components/layout/sidebar-user-nav/fallback'
import {
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/sv/sidebar'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useCompanyPath } from '@/lib/hooks'
import type {
  UserNavQuery,
  UserNavQueryVariables,
} from '@/components/layout/side-nav/user-nav.queries.types.gen'

const SidebarUserNavData = () => {
  const { logout } = use(AuthDispatchContext) ?? {}
  const { navigateToPath } = useCompanyPath()
  const { isMobile } = useSidebar()
  const { data } = useSuspenseQuery<UserNavQuery, UserNavQueryVariables>(
    USER_NAV_QUERY
  )
  const user = data?.user
  const userInitials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase()

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            tooltip={user?.fullName ?? 'Account'}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar className="size-8 rounded-lg">
              <AvatarImage src={user?.image ?? ''} alt={user?.fullName ?? ''} />
              <AvatarFallback className="rounded-lg">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <div className="overflow-hidden flex-1 transition-[max-width] duration-200 ease-linear max-w-40 group-data-[collapsible=icon]:max-w-0">
              <div className="grid text-left leading-tight">
                <span className="whitespace-nowrap text-sm font-medium">
                  {user?.fullName}
                </span>
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {user?.email}
                </span>
              </div>
            </div>
            <ChevronsUpDown className="size-4 transition-[max-width,opacity] duration-200 ease-linear max-w-4 group-data-[collapsible=icon]:max-w-0 group-data-[collapsible=icon]:opacity-0" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={isMobile ? 'bottom' : 'right'}
          align="end"
          sideOffset={4}
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="size-8 rounded-lg">
                <AvatarImage
                  src={user?.image ?? ''}
                  alt={user?.fullName ?? ''}
                />
                <AvatarFallback className="rounded-lg">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-sm font-medium">
                  {user?.fullName}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {user?.email}
                </span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem
              onClick={() => navigateToPath({ pathname: '/my-account' })}
              className="cursor-pointer"
            >
              <UserRound />
              Account
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => logout?.()}
            className="cursor-pointer"
          >
            <LogOut />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}

export const SidebarUserNav = () => (
  <Suspense fallback={<SidebarUserNavFallback />}>
    <SidebarUserNavData />
  </Suspense>
)
